import { useRef, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '@l2u/ui/lib/api.ts';
import { useConfirm } from '../lib/confirm.tsx';
import { Avatar } from '@l2u/ui/components/Avatar.tsx';
import { Btn } from '@l2u/ui/components/Btn.tsx';
import { NavIcon } from '@l2u/ui/components/NavIcon.tsx';
import { CenteredSpinner, Spinner } from '@l2u/ui/components/Spinner.tsx';
import { EmptyState } from '@l2u/ui/components/EmptyState.tsx';
import { Modal } from '@l2u/ui/components/Modal.tsx';
import { Field, Select, TextInput } from '@l2u/ui/components/Field.tsx';
import { ErrorMessage } from '@l2u/ui/components/ErrorMessage.tsx';
import { SearchableMultiSelect } from '../components/SearchableMultiSelect.tsx';
import {
  accessSummary,
  canDeleteUser,
  canEditName,
  canEditRole,
  canEditScope,
  effectiveAssignment,
  isSuperAdmin,
  listSummary,
  outOfReach,
  roleFilterOptions,
  roleLabel,
  roleOptionsFor,
  scopedOptions,
  scopeHint,
  type AdminRole,
} from '../lib/adminAccess.ts';
import { contractorTypeOptions, stationOptions } from '@l2u/ui/lib/companyInfo.ts';
import { usePortalSettings } from '@l2u/ui/lib/portalSettings.tsx';
import { useAdminAuth } from '../lib/auth/admin.tsx';
import type { AdminUserListItem } from '@l2u/ui/lib/types.ts';

// The canonical filter-chip order. Which of these a given viewer is actually
// OFFERED is narrowed at render time (see roleValues below); whether they may
// ASSIGN a role is roleOptionsFor's separate decision.
const ROLE_VALUES: AdminRole[] = ['viewer', 'reviewer', 'super_admin'];

// Build-time constant: the email domain bound to the SAML IdP — the same var
// the login page reads (auth/AdminLogin.tsx). Unset ⇒ the invite modal always
// shows the password-invite copy.
const SSO_DOMAIN = import.meta.env.VITE_ADMIN_SSO_DOMAIN;

// Display-only mirror of the API's isSsoDomainEmail
// (apps/api/src/lib/ssoUserLink.ts:151-157): exact, case-insensitive match on
// the part after the last '@'. The API remains the enforcement point.
function isSsoDomainEmail(email: string): boolean {
  const domain = (SSO_DOMAIN ?? '').trim().toLowerCase();
  if (!domain) return false;
  const at = email.lastIndexOf('@');
  if (at === -1) return false;
  return (
    email
      .slice(at + 1)
      .trim()
      .toLowerCase() === domain
  );
}

type ScopePatch = {
  assigned_stations?: string[] | null;
  assigned_contractor_types?: string[] | null;
};

// One inline scope column. Admins get an editable MultiSelect; everyone else
// sees a read-only text summary. Empty selection ("All …") = unrestricted.
// `disabledTitle` covers the case where the viewer otherwise CAN edit scope
// but not on this particular row/dimension (I1): the server's own refusal
// wording, surfaced as a tooltip instead of a bad_request the operator has to
// go find in the network tab. `reachHint`, when present, is rendered under an
// editable cell to name the values a save cannot touch (I2) -- otherwise an
// unticked box that reappears on refetch reads as a bug rather than D19's
// delta merge doing exactly what it should.
function ScopeCell({
  assigned,
  noun,
  options,
  editable,
  onChange,
  disabledTitle,
  reachHint,
}: {
  assigned: string[] | null;
  noun: string; // e.g. 'stations' | 'contractor types'
  options: string[];
  editable: boolean;
  onChange: (next: string[]) => void;
  disabledTitle?: string | undefined;
  reachHint?: string | undefined;
}) {
  if (!editable) {
    return (
      <span className="text-xs text-slate-600" title={disabledTitle}>
        {listSummary(assigned, noun)}
      </span>
    );
  }
  const selected = assigned ?? [];
  const capNoun = noun.charAt(0).toUpperCase() + noun.slice(1);
  return (
    <div>
      <SearchableMultiSelect
        label={selected.length ? capNoun : `All ${noun}`}
        ariaLabel={`Assign ${noun}`}
        unitLabel={noun}
        values={options}
        selected={selected}
        onChange={onChange}
      />
      {reachHint && <p className="mt-1 text-[11px] text-slate-400">{reachHint}</p>}
    </div>
  );
}

// Wording that names the fix, mirrored from the server's own refusal
// (apps/api/src/lib/adminScope.ts mergeScopeEdit) so the tooltip and the
// bad_request it is standing in for never drift apart.
const UNRESTRICTED_TARGET_TITLE = 'You cannot edit the access of an unrestricted user';

// initialInviteOpen/initialInviteRole are test seams: apps/admin has no DOM
// harness, so the only way to render the (closed-by-default) invite modal, or
// to exercise it with a role other than the 'viewer' default, is to mount it
// that way. Same useState-initialiser approach the filter/settings hydration
// tests rely on.
export function AdminUsers({
  initialInviteOpen = false,
  initialInviteRole = 'viewer',
}: { initialInviteOpen?: boolean; initialInviteRole?: AdminRole } = {}) {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const { user: currentUser, reload } = useAdminAuth();
  // Only a super admin can reach this page at all (the Settings route guard
  // and requireSuperAdminRole on every /api/users route). This stays as the
  // per-control belt-and-braces check it always was.
  const isAdmin = isSuperAdmin(currentUser?.role);
  const { settings } = usePortalSettings();

  const { data, isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () => adminApi.get<{ users: AdminUserListItem[] }>('/api/users'),
  });

  const [roleFilter, setRoleFilter] = useState<AdminRole[]>([]);
  const [search, setSearch] = useState('');
  const [inviteOpen, setInviteOpen] = useState(initialInviteOpen);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRole, setInviteRole] = useState<AdminRole>(initialInviteRole);
  // Task 13: optional station / contractor-type scoping at invite time. Empty
  // means unrestricted, matching the inline ScopeCell convention and the API's
  // "[] and null both store NULL" normalisation.
  const [inviteStations, setInviteStations] = useState<string[]>([]);
  const [inviteTypes, setInviteTypes] = useState<string[]>([]);
  const [inviteError, setInviteError] = useState<unknown>(null);
  const [inviteNotice, setInviteNotice] = useState<string | null>(null);

  // One row in edit mode at a time. The draft lives here, not in the row, so a
  // re-render from a query refetch cannot reset a half-typed name.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState('');
  // Guards commitName against firing twice for one commit: Enter unmounts the
  // TextInput, and removing the currently-focused DOM node dispatches a
  // synchronous blur, so the onKeyDown call and the onBlur it triggers both
  // invoke commitName for the same edit. setEditingId/setDraftName are async
  // state updates, so by the time the re-entrant call runs it still closes
  // over the SAME draftName/u.name as the first call -- a state-based check
  // would trip identically both times and let the duplicate PATCH through. A
  // ref mutates the instant it's assigned, with no render in between, so the
  // second call sees the first call's write regardless of how React batches
  // or defers the state updates.
  const committedRef = useRef(false);

  const inviteMutation = useMutation({
    mutationFn: (body: {
      email: string;
      name: string;
      role: AdminRole;
      assigned_stations: string[];
      assigned_contractor_types: string[];
    }) => adminApi.post<{ user: AdminUserListItem; emailSent?: boolean }>('/api/users', body),
    onSuccess: (res) => {
      // emailSent is only present on SSO-domain invites; false means the row
      // was created but the notification email failed — surface it without
      // blocking anything.
      setInviteNotice(
        res.emailSent === false
          ? 'User added, but the notification email couldn’t be sent. They can still sign in with SSO.'
          : null,
      );
      qc.invalidateQueries({ queryKey: ['users'] });
      closeInvite();
    },
    onError: (err) => setInviteError(err),
  });

  function closeInvite() {
    setInviteOpen(false);
    setInviteEmail('');
    setInviteName('');
    setInviteRole('viewer');
    setInviteStations([]);
    setInviteTypes([]);
    setInviteError(null);
  }

  // Review finding 4: a failed rename, role change or scope edit used to snap
  // the row back with no message. Mirrors inviteError/inviteMutation above.
  const [updateError, setUpdateError] = useState<unknown>(null);
  const updateMutation = useMutation({
    mutationFn: (input: { id: string; patch: { role?: AdminRole; name?: string } & ScopePatch }) =>
      adminApi.patch(`/api/users/${input.id}`, input.patch),
    onSuccess: () => {
      setUpdateError(null);
      qc.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err) => setUpdateError(err),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.delete(`/api/users/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });

  function saveScope(u: AdminUserListItem, patch: ScopePatch) {
    updateMutation.mutate(
      { id: u.id, patch },
      {
        onSuccess: () => {
          // Own scope changed → refresh sidebar + drop now-out-of-scope cached rows.
          if (u.id === currentUser?.id) {
            void reload();
            void qc.invalidateQueries();
          }
        },
      },
    );
  }

  // D9: a role change never touches the scope columns, so demoting a super
  // admin returns them to whatever the row already holds -- the same value the
  // table has been showing them all along now that scope is live for every
  // role (D14). Say what that is before writing anyway: it is a confirm, not a
  // forced re-scope, and the residual case (someone never scoped comes back
  // unrestricted) is disclosed, not prevented.
  async function changeRole(u: AdminUserListItem, next: AdminRole) {
    if (next === u.role) return;
    if (isSuperAdmin(u.role)) {
      const ok = await confirm({
        title: 'Demote super admin',
        body: (
          <>
            After demotion <span className="font-semibold">{u.email}</span> will see:{' '}
            <span className="font-semibold">
              {accessSummary(u.assigned_stations, u.assigned_contractor_types)}
            </span>
          </>
        ),
        confirmLabel: 'Demote',
        danger: true,
      });
      // The <Select> is controlled by the server row, so declining simply
      // re-renders it at u.role.
      if (!ok) return;
    }
    updateMutation.mutate({ id: u.id, patch: { role: next } });
  }

  function startEditName(u: AdminUserListItem) {
    setEditingId(u.id);
    setDraftName(u.name);
    committedRef.current = false;
  }

  // Enter or blur commits; Escape cancels (see cancelEditName below, which
  // claims the same committedRef slot so the blur Escape provokes cannot
  // resurrect the abandoned draft). Trimmed-empty or unchanged also sends
  // nothing. No new endpoint: PATCH /api/users/:id has accepted `name` since
  // the route was written.
  //
  // committedRef also blocks the re-entrant call an Enter-commit produces
  // (see the ref's declaration above) -- without it, this fires the same
  // PATCH twice.
  function commitName(u: AdminUserListItem) {
    if (committedRef.current) return;
    committedRef.current = true;
    const next = draftName.trim();
    setEditingId(null);
    if (!next || next === u.name) return;
    updateMutation.mutate(
      { id: u.id, patch: { name: next } },
      {
        onSuccess: () => {
          // Own name changed → refresh the sidebar footer, which reads
          // useAdminAuth's user, not the users query.
          if (u.id === currentUser?.id) void reload();
        },
      },
    );
  }

  // Escape must throw the edit away. Closing the editor unmounts the focused
  // TextInput, and removing the focused DOM node dispatches a synchronous
  // blur -- the same re-entrancy documented at the committedRef declaration
  // above (Users.tsx:131-141), reached from the cancel side instead of the
  // Enter side. That blur runs the onBlur closure from the PREVIOUS render, so
  // it still closes over the typed draftName; setDraftName below only QUEUES a
  // state update and cannot change what that stale closure sees. Without a
  // guard, commitName would find committedRef false, compute `next` = the
  // typed value, compare it against the old u.name, and PATCH the very edit
  // the user just abandoned.
  //
  // So claim the commit slot the same way commitName does: a ref assignment,
  // which lands the instant it executes with no render in between, and is
  // therefore visible to the blur no matter how React batches or defers the
  // state updates. startEditName clears the flag again (Users.tsx:230-234) and
  // is the only way back into edit mode, so the next edit -- of this same user
  // or any other -- still commits normally.
  //
  // setDraftName(u.name) is now only cosmetic tidying of the abandoned draft;
  // committedRef is what actually cancels.
  function cancelEditName(u: AdminUserListItem) {
    committedRef.current = true;
    setDraftName(u.name);
    setEditingId(null);
  }

  if (isLoading) return <CenteredSpinner />;
  const users = data?.users ?? [];
  // Offer only the roles this roster actually contains — see roleFilterOptions
  // (lib/adminAccess.ts) for why it derives from the rows and not the viewer's
  // role. An empty roster yields the full list, which never renders: the
  // users.length === 0 branch shows the EmptyState instead of the filter bar.
  const roleValues = roleFilterOptions(ROLE_VALUES, users, roleFilter);
  const term = search.trim().toLowerCase();
  const visibleUsers = users.filter((u) => {
    if (roleFilter.length && !roleFilter.includes(u.role)) return false;
    if (term && !(u.name.toLowerCase().includes(term) || u.email.toLowerCase().includes(term)))
      return false;
    return true;
  });

  function onInviteSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    // The scope pickers now render for every role, including super admin
    // (D14 -- a super admin's scope is live, not a special case), so the
    // picked values are sent as-is. The server clamps them to the inviter's
    // own reach regardless of what is submitted here.
    inviteMutation.mutate({
      email: inviteEmail.trim(),
      name: inviteName.trim(),
      role: inviteRole,
      assigned_stations: inviteStations,
      assigned_contractor_types: inviteTypes,
    });
  }

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Users</h1>
          <p className="mt-1 text-sm text-slate-500">
            Manage admin and reviewer access to the portal.
          </p>
        </div>
        {isAdmin && (
          <Btn
            onClick={() => {
              setInviteNotice(null);
              setInviteOpen(true);
            }}
          >
            Invite user
          </Btn>
        )}
      </div>

      {inviteNotice && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-3 text-sm text-amber-800">
          {inviteNotice}
        </div>
      )}

      <ErrorMessage error={updateError} />

      {users.length === 0 ? (
        <EmptyState title="No users yet" description="Invite your first admin to get started." />
      ) : (
        <>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="sm:w-72">
              <Field label="Search">
                <TextInput
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name or email"
                />
              </Field>
            </div>
            <SearchableMultiSelect
              label="Role"
              unitLabel="roles"
              values={roleValues}
              labelFor={roleLabel}
              selected={roleFilter}
              onChange={(next) => setRoleFilter(next as AdminRole[])}
            />
          </div>
          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3">Station</th>
                  <th className="px-4 py-3">Contractor type</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {visibleUsers.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-sm text-slate-500">
                      No users match your search.
                    </td>
                  </tr>
                ) : (
                  visibleUsers.map((u) => {
                    const isSelf = u.id === currentUser?.id;
                    // I1: a scoped granter reaching an unrestricted-in-that-
                    // dimension target hits mergeScopeEdit's bad_request on
                    // save (adminScope.ts) -- lock the cell before that
                    // round-trip rather than after. Stations has no such case
                    // (see the comment at the stations ScopeCell); contractor
                    // types does, because D2 keeps it out of canSeeUser.
                    const contractorTypesLocked =
                      effectiveAssignment(currentUser?.assigned_contractor_types) !== null &&
                      effectiveAssignment(u.assigned_contractor_types) === null;
                    // I2: name what a save cannot touch, so an unticked value
                    // that reappears on refetch reads as D19's delta merge
                    // doing its job rather than as a bug.
                    const stationsOutOfReach = outOfReach(
                      effectiveAssignment(currentUser?.assigned_stations),
                      u.assigned_stations ?? null,
                    );
                    const typesOutOfReach = outOfReach(
                      effectiveAssignment(currentUser?.assigned_contractor_types),
                      u.assigned_contractor_types ?? null,
                    );
                    return (
                      <tr key={u.id}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <Avatar name={u.name} size={32} />
                            {editingId === u.id ? (
                              <TextInput
                                autoFocus
                                aria-label={`Edit name of ${u.name}`}
                                maxLength={120}
                                value={draftName}
                                onChange={(e) => setDraftName(e.target.value)}
                                onBlur={() => commitName(u)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    commitName(u);
                                  } else if (e.key === 'Escape') {
                                    cancelEditName(u);
                                  }
                                }}
                              />
                            ) : canEditName(currentUser?.role) ? (
                              <button
                                type="button"
                                title="Edit name"
                                onClick={() => startEditName(u)}
                                className="rounded font-medium text-slate-900 hover:underline focus:outline-none focus:ring-2 focus:ring-slate-300"
                              >
                                {u.name}
                              </button>
                            ) : (
                              <span className="font-medium text-slate-900">{u.name}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-slate-700">{u.email}</td>
                        <td className="px-4 py-3">
                          {canEditRole(currentUser?.role, u.role, isSelf) ? (
                            <Select
                              value={u.role}
                              onChange={(e) => void changeRole(u, e.target.value as AdminRole)}
                            >
                              {roleOptionsFor(currentUser?.role).map((r) => (
                                <option key={r} value={r}>
                                  {roleLabel(r)}
                                </option>
                              ))}
                            </Select>
                          ) : (
                            <span className="inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
                              {roleLabel(u.role)}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {/* Assignment options narrow to the GRANTING admin's
                              own EFFECTIVE scope -- an admin cannot grant wider
                              than they hold (the server's D19 delta merge is
                              what actually enforces that; this is display, not
                              enforcement). Any admin or above may edit scope
                              now (D17). The target's saved assignments fold in
                              so a revoked or out-of-scope value stays visible
                              and removable. `assigned` goes through
                              effectiveAssignment purely to normalize [] to
                              null (D15) -- it no longer branches on the
                              target's role.

                              No unrestricted-target lock here (I1): canSeeUser
                              rule 3 already hides an unrestricted target from
                              a scoped viewer, so a scoped admin's roster never
                              contains a row this cell could 400 on for
                              stations -- unlike contractor types, which don't
                              filter the roster (D2). */}
                          <ScopeCell
                            assigned={effectiveAssignment(u.assigned_stations)}
                            noun="stations"
                            options={scopedOptions(
                              stationOptions(settings),
                              effectiveAssignment(currentUser?.assigned_stations),
                              u.assigned_stations ?? [],
                            )}
                            editable={canEditScope(currentUser?.role)}
                            onChange={(next) => saveScope(u, { assigned_stations: next })}
                            reachHint={
                              stationsOutOfReach.length
                                ? `${stationsOutOfReach.join(', ')} preserved -- outside your reach`
                                : undefined
                            }
                          />
                        </td>
                        <td className="px-4 py-3">
                          {/* I1: contractor types never filter the roster
                              (D2), so a scoped granter CAN reach a target
                              whose types are unrestricted -- exactly the case
                              mergeScopeEdit refuses with "You cannot edit the
                              access of an unrestricted user" (adminScope.ts).
                              Lock the cell before the save round-trips into
                              that 400, and say why via the same wording. */}
                          <ScopeCell
                            assigned={effectiveAssignment(u.assigned_contractor_types)}
                            noun="contractor types"
                            options={scopedOptions(
                              contractorTypeOptions(settings),
                              effectiveAssignment(currentUser?.assigned_contractor_types),
                              u.assigned_contractor_types ?? [],
                            )}
                            editable={canEditScope(currentUser?.role) && !contractorTypesLocked}
                            disabledTitle={
                              contractorTypesLocked ? UNRESTRICTED_TARGET_TITLE : undefined
                            }
                            onChange={(next) => saveScope(u, { assigned_contractor_types: next })}
                            reachHint={
                              typesOutOfReach.length
                                ? `${typesOutOfReach.join(', ')} preserved -- outside your reach`
                                : undefined
                            }
                          />
                        </td>
                        <td className="px-4 py-3 text-right">
                          {canDeleteUser(currentUser?.role, isSelf) ? (
                            <button
                              type="button"
                              aria-label={`Remove ${u.name}`}
                              title="Remove user"
                              disabled={
                                deleteMutation.isPending && deleteMutation.variables === u.id
                              }
                              onClick={() => {
                                void (async () => {
                                  if (
                                    await confirm({
                                      title: 'Remove user',
                                      body: (
                                        <>
                                          Are you sure you want to remove{' '}
                                          <span className="font-semibold">{u.email}</span>?
                                          They&apos;ll lose access immediately.
                                        </>
                                      ),
                                      confirmLabel: 'Remove',
                                      danger: true,
                                    })
                                  )
                                    deleteMutation.mutate(u.id);
                                })();
                              }}
                              className="inline-flex items-center justify-center rounded-md p-1.5 text-red-600 transition hover:bg-red-50 hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              {deleteMutation.isPending && deleteMutation.variables === u.id ? (
                                <Spinner size={16} />
                              ) : (
                                <NavIcon name="trash" size={16} />
                              )}
                            </button>
                          ) : (
                            <span className="text-xs text-slate-400">
                              {isSelf ? 'You' : 'Super admin only'}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      <Modal
        open={inviteOpen}
        onClose={closeInvite}
        title="Invite user"
        footer={
          <>
            <Btn variant="secondary" onClick={closeInvite}>
              Cancel
            </Btn>
            <Btn
              type="submit"
              form="invite-form"
              loading={inviteMutation.isPending}
              disabled={!inviteEmail.trim() || !inviteName.trim()}
            >
              Send invitation
            </Btn>
          </>
        }
      >
        <form id="invite-form" className="space-y-4" onSubmit={onInviteSubmit}>
          <Field label="Email" required>
            <TextInput
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              required
            />
          </Field>
          <Field label="Name" required>
            <TextInput
              value={inviteName}
              onChange={(e) => setInviteName(e.target.value)}
              required
            />
          </Field>
          <Field label="Role">
            {/* "Super admin" is offered only to super admins (roleOptionsFor);
                the API's role clamp refuses it from anyone else. */}
            <Select value={inviteRole} onChange={(e) => setInviteRole(e.target.value as AdminRole)}>
              {roleOptionsFor(currentUser?.role).map((r) => (
                <option key={r} value={r}>
                  {roleLabel(r)}
                </option>
              ))}
            </Select>
          </Field>
          {/* Review finding 5 / the D14 headline capability: scope is
              orthogonal to tier, so these pickers apply to every invited
              role, including super admin -- an inviter may mint a SCOPED
              super admin ("station owner"), and hiding the pickers for that
              role would make that impossible from the UI even though the API
              accepts it. Reuses ScopeCell so the control and its "All …"
              empty state are identical to the inline editors in the users
              table. The option lists narrow to the inviter's EFFECTIVE
              scope -- live for every role now, including a super admin's
              own (D14). */}
          <Field
            label="Stations"
            hint={scopeHint(effectiveAssignment(currentUser?.assigned_stations), 'stations')}
          >
            <ScopeCell
              assigned={inviteStations}
              noun="stations"
              options={scopedOptions(
                stationOptions(settings),
                effectiveAssignment(currentUser?.assigned_stations),
              )}
              editable
              onChange={setInviteStations}
            />
          </Field>
          <Field
            label="Contractor types"
            hint={scopeHint(
              effectiveAssignment(currentUser?.assigned_contractor_types),
              'contractor types',
            )}
          >
            <ScopeCell
              assigned={inviteTypes}
              noun="contractor types"
              options={scopedOptions(
                contractorTypeOptions(settings),
                effectiveAssignment(currentUser?.assigned_contractor_types),
              )}
              editable
              onChange={setInviteTypes}
            />
          </Field>
          <p className="text-xs text-slate-500">
            {isSsoDomainEmail(inviteEmail.trim())
              ? "They'll be notified by email and will sign in with SSO — no password needed."
              : 'An invitation email will be sent. The user sets their password from the link, ' +
                'then signs in with their email and password.'}
          </p>
          <ErrorMessage error={inviteError} />
        </form>
      </Modal>
    </div>
  );
}
